import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Button,
  Box,
  Paper,
  TextField,
  MenuItem,
  Tooltip,
  Divider,
  Autocomplete,
  Alert,
  Chip,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  InfoOutlined as InfoIcon,
  ErrorOutline as ErrorIcon,
} from "@mui/icons-material";

/* =============================================================
 * SecurityGroupRulesAccordion
 * ============================================================= */

const SecurityGroupRulesAccordion = ({
  formConfig = [],
  formData,
  formSubmitted,
  setFormData,
  errors: externalErrors = {},
  onValidationChange,
  sgTouched,
  api,
  apiContext,
  sourceValueOptions,
}) => {
  const inboundField = useMemo(
    () => formConfig.find((f) => f.fieldName === "InboundRules"),
    [formConfig]
  );
  const outboundField = useMemo(
    () => formConfig.find((f) => f.fieldName === "OutboundRules"),
    [formConfig]
  );

  const [inboundOpen, setInboundOpen] = useState(false);
  const [outboundOpen, setOutboundOpen] = useState(true);



  // Compute validation rules locally
  const liveValidation = useMemo(
    () => validateSecurityGroupRules(formConfig, formData, formSubmitted),
    [formConfig, formData]
  );

  const nextValidation = useMemo(
    () => validateSGForNext(formData),
    [formData]
  );

  const totalErrorsCount = useMemo(() => {
    let count = 0;
    ["InboundRules", "OutboundRules"].forEach((group) => {
      const groupErrs = liveValidation.errors[group] || {};
      if (groupErrs._group) count++;
      Object.keys(groupErrs).forEach((key) => {
        if (key !== "_group") {
          count += Object.keys(groupErrs[key] || {}).length;
        }
      });
    });
    return count;
  }, [liveValidation.errors]);

  const isValid = nextValidation.isValid && totalErrorsCount === 0;
  const nextErrorString = nextValidation.error || "";


  useEffect(() => {
    if (!sgTouched) return;

    const hasErrors =
      Object.keys(liveValidation.errors || {}).length > 0;

    if (hasErrors) {
      setInboundOpen(true);
      setOutboundOpen(true);
    }
  }, [sgTouched, liveValidation.errors]);

  useEffect(() => {
    if (!onValidationChange) return;
    onValidationChange({
      isValid,
      nextError: nextErrorString
    });
  }, [isValid, nextErrorString, onValidationChange]);

if (!inboundField && !outboundField) return null;

  const mergeErrors = (groupName) => {
    //  if (!sgTouched) return {};
    return {
      ...(externalErrors[groupName] || {}),
      ...(liveValidation.errors[groupName] || {}),
    };
  };




  const commonSectionProps = {
    formData,
    setFormData,
    api,
    apiContext,
    sourceValueOptions,
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
      {inboundField && (
        <RulesSection
          field={inboundField}
          direction="inbound"
          expanded={inboundOpen}
          formSubmitted={formSubmitted}
          sgTouched={sgTouched}
          onToggle={setInboundOpen}
          errors={mergeErrors("InboundRules")}
          warnings={liveValidation.warnings?.InboundRules || {}}
          {...commonSectionProps}
        />
      )}

      {outboundField && (
        <RulesSection
          field={outboundField}
          direction="outbound"
          expanded={outboundOpen}
          formSubmitted={formSubmitted}
          sgTouched={sgTouched}
          onToggle={setOutboundOpen}
          errors={mergeErrors("OutboundRules")}
          warnings={liveValidation.warnings?.OutboundRules || {}}
          {...commonSectionProps}
        />
      )}
    </Box>
  );
};

/* ============================================================= */
/* Autocomplete options — fetch + cache                           */
/* ============================================================= */

const _optionsCache = new Map();

const resolveUrlTemplate = (tpl, ctx = {}) => {
  if (!tpl) return "";
  let url = tpl.startsWith("/") || /^https?:/i.test(tpl) ? tpl : `/${tpl}`;
  return url.replace(/\{(\w+)\}/g, (_, k) =>
    ctx[k] !== undefined && ctx[k] !== null ? encodeURIComponent(ctx[k]) : ""
  );
};

const normalizeTargetsResponse = (raw) => {
  if (!raw) return [];
  const flat = [];
  if (Array.isArray(raw)) {
    if (raw.length && raw[0] && Array.isArray(raw[0].items)) {
      raw.forEach((g) => {
        (g.items || []).forEach((it) => {
          flat.push({
            label: it.label ?? it.value,
            value: it.value,
            group: g.groupLabel || "Options",
            type: it.type,
          });
        });
      });
      return flat;
    }
    return raw.map((o) =>
      typeof o === "string"
        ? { label: o, value: o, group: "Options" }
        : {
          label: o.label ?? o.value,
          value: o.value,
          group: o.group || o.groupLabel || "Options",
          type: o.type,
        }
    );
  }
  return [];
};

const useSourceValueOptions = ({
  api,
  apiContext,
  dropdownSource,
  preloaded,
}) => {
  const [options, setOptions] = useState(preloaded || []);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (preloaded && preloaded.length) {
      setOptions(preloaded);
      return;
    }
    if (!api || !dropdownSource) return;

    const url = resolveUrlTemplate(dropdownSource, apiContext || {});
    if (!url || /\{\w+\}/.test(url)) return;

    let cancelled = false;
    setLoading(true);

    let p = _optionsCache.get(url);
    if (!p) {
      p = api
        .get(url, {
          headers: {
            accept: "*/*",
            "X-CORRELATION-ID":
              typeof crypto !== "undefined" && crypto.randomUUID
                ? crypto.randomUUID()
                : String(Date.now()),
          },
        })
        .then((res) => normalizeTargetsResponse(res?.data))
        .catch(() => []);
      _optionsCache.set(url, p);
    }

    p.then((fetched) => {
      if (cancelled) return;
      const merged = [...(fetched || [])];
      const seen = new Set();
      const deduped = merged.filter((o) => {
        if (seen.has(o.value)) return false;
        seen.add(o.value);
        return true;
      });
      setOptions(deduped);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [api, apiContext, dropdownSource, preloaded]);

  return { options, loading };
};

/* ============================================================= */
/* Debounced Autocomplete Input Wrapper to prevent infinite loop  */
/* ============================================================= */
const DebouncedAutocompleteField = ({
  value,
  onChange,
  options,
  loading,
  placeholder,
  error,
  helperText,
}) => {
  const [localInput, setLocalInput] = useState(value || "");
  const debounceTimeoutRef = useRef(null);

  // Sync local input value with incoming prop value (e.g. if updated externally or initialized)
  useEffect(() => {
    setLocalInput(value || "");
  }, [value]);

  const debouncedOnChange = useCallback(
    (nextVal) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = setTimeout(() => {
        onChange(nextVal);
      }, 180); // Quick debounce to interrupt fast keypress loops
    },
    [onChange]
  );

  const handleInputChange = (newVal) => {
    setLocalInput(newVal);
    debouncedOnChange(newVal);
  };

  // Safe Option Lookup
  const mappedValue = useMemo(() => {
    return options.find((o) => o.value === value) || value || null;
  }, [options, value]);

  return (
    <Autocomplete
      freeSolo
      // size="medium"
      options={options}
      loading={loading}
      groupBy={(o) => o.group || "Suggestions"}
      getOptionLabel={(o) => (typeof o === "string" ? o : o.label ?? o.value ?? "")}
      value={mappedValue}
      inputValue={localInput}
      onChange={(_, newVal) => {
        const v = typeof newVal === "string" ? newVal : newVal?.value || "";
        handleInputChange(v);
      }}
      onInputChange={(_, newInput, reason) => {
        if (reason === "input" || reason === "clear") {
          handleInputChange(newInput);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

/* ============================================================= */
/* Section — one outer accordion (Inbound OR Outbound)            */
/* ============================================================= */
const RulesSection = ({
  field,
  direction,
  expanded,
  onToggle,
  formData,
  formSubmitted,
  RulesSection,
  setFormData,
  errors,
  warnings,
  api,
  apiContext,
  sourceValueOptions,
  sgTouched,
}) => {


  const items = useMemo(() => {
    return Array.isArray(formData?.[field.fieldName]?.value)
      ? formData[field.fieldName].value
      : [];
  }, [formData, field.fieldName]);

  const collectionFields = field.collectionFields || [];
  const maxCount = field.maxCount || 60;

  const cfMap = useMemo(() => {
    const m = {};
    collectionFields.forEach((c) => (m[c.fieldName] = c));
    return m;
  }, [collectionFields]);

  const T = direction === "inbound" ? "Inbound" : "Outbound";
  const typeFieldName = `${T}Type`;
  const protocolFieldName = `${T}Protocol`;
  const portFieldName = `${T}PortRange`;
  const sourceDestFieldName =
    direction === "inbound" ? "InboundSourceType" : "OutboundDestinationType";
  const sourceDestValueFieldName =
    direction === "inbound" ? "InboundSourceValue" : "OutboundDestinationValue";
  const descriptionFieldName = `${T}Description`;

  const parseJSON = (raw, fallback) => {
    if (!raw) return fallback;
    try {
      return JSON.parse(raw);
    } catch {
      return fallback;
    }
  };

  const typeOptions = useMemo(
    () => parseJSON(cfMap[typeFieldName]?.dropdownValues, []),
    [cfMap, typeFieldName]
  );
  const sourceDestOptions = useMemo(
    () =>
      parseJSON(cfMap[sourceDestFieldName]?.dropdownValues, [
        { label: "Custom", value: "Custom" },
      ]),
    [cfMap, sourceDestFieldName]
  );


  const sourceDestValueCfg = cfMap[sourceDestValueFieldName];
  const isAutocompleteValue = sourceDestValueCfg?.fieldType === "autocomplete";

  const { options: sourceDestValueSuggestions, loading: optionsLoading } =
    useSourceValueOptions({
      api,
      apiContext,
      dropdownSource: isAutocompleteValue
        ? sourceDestValueCfg?.dropdownSource
        : null,
      preloaded: sourceValueOptions,
    });

  const buildDefaultRule = useCallback(() => {
    const defType =
      typeOptions.find((o) => o.isDefault) ||
      typeOptions[0] || {
        value: "",
        label: "",
        protocol: "",
        portRange: "",
        isPortEditable: true,
        isProtocolEditable: false,
      };

    // const rule = {};
    // collectionFields.forEach((cf) => {
    //   rule[cf.fieldName] = {
    //     value: cf.defaultValue || "",
    //     label: cf.defaultValue || "",
    //   };
    // });

    const rule = {};

    collectionFields.forEach((cf) => {
      if (cf.fieldType === "dropdown" && cf.dropdownValues) {
        const options = JSON.parse(cf.dropdownValues || "[]");
        const defaultOption =
          options.find((o) => o.isDefault) || options[0];

        rule[cf.fieldName] = {
          value: defaultOption?.value || "",
          label: defaultOption?.label || "",
        };
      } else {
        rule[cf.fieldName] = {
          value: cf.defaultValue || "",
          label: cf.defaultValue || "",
        };
      }
    });


    rule[typeFieldName] = { value: defType.value, label: defType.label };
    rule[protocolFieldName] = {
      value: defType.protocol || "",
      label: defType.protocol || "",
    };
    rule[portFieldName] = {
      value: String(defType.portRange ?? ""),
      label: String(defType.portRange ?? ""),
    };
    // rule.__meta = {
    //   isProtocolEditable: !!defType.isProtocolEditable,
    //   isPortEditable: !!defType.isPortEditable,
    // };

    rule.__meta = {
      // touched: false,
      destinationValueTouched: false,
      isNew: true,
      isExisting: false,
      isProtocolEditable: !!defType.isProtocolEditable,
      isPortEditable: !!defType.isPortEditable,
    };




    return rule;
  }, [
    typeOptions,
    collectionFields,
    typeFieldName,
    protocolFieldName,
    portFieldName,
  ]);

  useEffect(() => {
    if (direction === "inbound" && collectionFields.length > 0) {

      setFormData((prev) => {
        const currentField = prev[field.fieldName] || {};
    
        if (currentField.__initialized) {
          return prev;
        }

        if (Array.isArray(currentField.value) && currentField.value.length > 0) {
          return {
            ...prev,
            [field.fieldName]: {
              ...currentField,
              __initialized: true,
            },
          };
        }

 
        const first = buildDefaultRule();
        return {
          ...prev,
          [field.fieldName]: {
            value: [first],
            label: "1 rule",
            __initialized: true,
          },
        };
      });
    }
  }, []); 

  // useEffect(() => {
  //   if (
  //     field.fieldName === "OutboundRules" &&
  //     items.length === 0 &&
  //     collectionFields.length > 0
  //   ) {
  //     const first = buildDefaultRule();

  //     // first.OutboundDestinationValue = {
  //     //   value: "0.0.0.0/0",
  //     //   label: "0.0.0.0/0",
  //     // };

  //     setFormData((prev) => ({
  //       ...prev,
  //       [field.fieldName]: {
  //         value: [first],
  //         label: "1 rule",
  //       },
  //     }));
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  const commit = (nextItems) => {
    setFormData((prev) => ({
      ...prev,
      [field.fieldName]: {
        ...(prev[field.fieldName] || {}),
        value: nextItems,
        label: `${nextItems.length} rule${nextItems.length === 1 ? "" : "s"}`,
      },
    }));
  };

  const addRule = () => {
    if (items.length >= maxCount) return;
    commit([...items, buildDefaultRule()]);
  };
  const removeRule = (idx) => commit(items.filter((_, i) => i !== idx));

  const updateCell = (idx, fieldName, value, label) => {

    const next = items.map((row, i) => {
      if (i !== idx) return row;
      // const updated = {
      //   ...row,
      //   [fieldName]: { value, label: label ?? value },
      // };

      const updated = {
        ...row,
        [fieldName]: { value, label: label ?? value },
        // __meta: {
        //   ...row.__meta,
        //   touched: true,
        //    isNew: false,
        // }
      };

      if (fieldName === sourceDestValueFieldName) {
        updated.__meta = {
          ...updated.__meta,
          destinationValueTouched: true,
        };
      }

      if (fieldName === typeFieldName) {
        const meta = typeOptions.find((o) => o.value === value);
        if (meta) {
          updated[protocolFieldName] = {
            value: meta.protocol || "",
            label: meta.protocol || "",
          };
          updated[portFieldName] = {
            value: String(meta.portRange ?? ""),
            label: String(meta.portRange ?? ""),
          };

          //           updated.__meta = {
          //   ...updated.__meta,
          //   touched: true,
          //   isNew: false,
          //   isProtocolEditable: !!meta.isProtocolEditable,
          //   isPortEditable: !!meta.isPortEditable,
          // };

          updated.__meta = {
            ...updated.__meta,
            isProtocolEditable: !!meta.isProtocolEditable,
            isPortEditable: !!meta.isPortEditable,
          };

        }
      }
      return updated;
    });
    commit(next);
  };

  const handlePortChange = (idx, raw) => {
    const cleaned = raw.replace(/[^0-9\-]/g, "");
    const parts = cleaned.split("-");
    const safe =
      parts.length > 2 ? `${parts[0]}-${parts.slice(1).join("")}` : cleaned;
    updateCell(idx, portFieldName, safe);
  };

  const errorCount = Object.keys(errors || {}).filter((k) => k !== "_group")
    .length;

  const summary = `${items.length} ${direction === "inbound" ? "Inbound" : "Outbound"} rule${items.length === 1 ? "" : "s"
    }`;

 return (
    <Box sx={{ width: "100%", mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {field.fieldLabel?.replace("Rules", "").trim()} Rules
        </Typography>
        {items.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            ({summary})
          </Typography>
        )}
      </Box>

      {errors?._group && (
        <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
          {errors._group}
        </Alert>
      )}

      {items.length === 0 && (
        <Box>
          <Typography color="text.secondary">
            This security group has no {direction === "inbound" ? "inbound" : "outbound"} rules.
          </Typography>
        </Box>
      )}

      {items.map((row, idx) => {
        const rowErrors = errors?.[idx] || {};
        const meta = row.__meta || {};
        
        const showDestinationValueError = formSubmitted || meta.destinationValueTouched;
        const showErrors = formSubmitted || meta.touched || (sgTouched && !meta.isNew);
        
        const descVal = row[descriptionFieldName]?.value || "";
        const sdTypeVal = row[sourceDestFieldName]?.value;
        const sdValueVal = row[sourceDestValueFieldName]?.value || "";
        const protocolVal = row[protocolFieldName]?.value || "";

        return (
          <Accordion
            key={idx}
            defaultExpanded
            disableGutters
            sx={{
              mb: 1.5,
              border: (t) => `1px solid ${t.palette.divider}`,
              borderRadius: "8px !important",
              boxShadow: "none",
              "&:before": { display: "none" },
            }}
          >
            {/* Accordion Header (Summary) */}
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  pr: 2,
                }}
              >
                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                  <Typography sx={{ fontWeight: 600 }}>Rule {idx + 1}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {protocolVal}
                  </Typography>
                </Box>
                
                {!meta.isExisting && (
                  <Typography
                    color="error"
                    sx={{ cursor: "pointer", fontSize: "0.875rem", fontWeight: 500 }}
                    onClick={(e) => {
                      e.stopPropagation(); // Prevents the accordion from toggling when clicking Remove
                      removeRule(idx);
                    }}
                  >
                    Remove
                  </Typography>
                )}
              </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ p: 3, pt: 1 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                    md: "repeat(3, 1fr)",
                  },
                  gap: 2,
                }}
              >
                {/* Type */}
                <TextField
                  select
                  fullWidth
                  label="Type"
                  value={row[typeFieldName]?.value || ""}
                  onChange={(e) => updateCell(idx, typeFieldName, e.target.value)}
                  error={showErrors && !!rowErrors[typeFieldName]}
                  helperText={showErrors ? rowErrors[typeFieldName] || "" : ""}
                  disabled={meta.isEditable === false}      
                  slotProps={{
    select: {
      MenuProps: {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "left",
        },
        transformOrigin: {
          vertical: "top",
          horizontal: "left",
        },
        slotProps: {
          paper: {
            sx: {
              maxHeight: 250,
              mt: 0.5,        
            },
          },
        },
      },
    },
  }}   
                >
                  {typeOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>

                {/* Protocol */}
                <TextField
                  fullWidth
                  label="Protocol"
                  value={row[protocolFieldName]?.value || ""}
                  onChange={(e) => updateCell(idx, protocolFieldName, e.target.value)}
                  // disabled={!meta.isProtocolEditable}
                   disabled={meta.isEditable === false || !meta.isProtocolEditable9}
                  error={showErrors && !!rowErrors[protocolFieldName]}
                  helperText={showErrors ? rowErrors[protocolFieldName] || "" : ""}
                />

                {/* Port range */}
                <TextField
                  fullWidth
                  label="Port range"
                  value={row[portFieldName]?.value || ""}
                  onChange={(e) => handlePortChange(idx, e.target.value)}
                  onKeyDown={(e) => {
                    if (
                      e.key.length === 1 &&
                      !/[0-9\-]/.test(e.key) &&
                      !e.ctrlKey &&
                      !e.metaKey
                    ) {
                      e.preventDefault();
                    }
                  }}
                  disabled={ meta?.isEditable === false || !meta.isPortEditable}
                  placeholder="e.g. 80 or 80-443"
                  inputProps={{ inputMode: "numeric" }}
                  error={showErrors && !!rowErrors[portFieldName]}
                  helperText={
                    showErrors
                      ? rowErrors[portFieldName] || "Numbers and hyphen only (e.g. 80, 80-443)"
                      : "Numbers and hyphen only (e.g. 80, 80-443)"
                  }
                />

                {/* Source / Destination type */}
                <TextField
                  select
                  fullWidth
                  label={direction === "inbound" ? "Source" : "Destination"}
                  value={sdTypeVal}
                  onChange={(e) => updateCell(idx, sourceDestFieldName, e.target.value)}
                  error={showErrors && !!rowErrors[sourceDestFieldName]}
                  helperText={showErrors ? rowErrors[sourceDestFieldName] || "" : ""}
                >
                  {sourceDestOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>

                {isAutocompleteValue ? (
                  <DebouncedAutocompleteField
                    value={sdValueVal}
                    label={direction === "inbound" ? "Source value" : "Destination value"}
                    options={sourceDestValueSuggestions}
                    loading={optionsLoading}
                    placeholder={
                      sourceDestValueCfg?.placeholder || "CIDR block or Security Group ID"
                    }
                    error={showDestinationValueError && !!rowErrors[sourceDestValueFieldName]}
                    helperText={
                      showDestinationValueError ? rowErrors[sourceDestValueFieldName] || "" : ""
                    }
                    onChange={(newVal) => updateCell(idx, sourceDestValueFieldName, newVal)}
                  />
                ) : (
                  <TextField
                    fullWidth
                    value={sdValueVal}
                    placeholder={sourceDestValueCfg?.placeholder || ""}
                    onChange={(e) => updateCell(idx, sourceDestValueFieldName, e.target.value)}
                    error={showDestinationValueError && !!rowErrors[sourceDestValueFieldName]}
                    helperText={
                      showDestinationValueError ? rowErrors[sourceDestValueFieldName] || "" : ""
                    }
                  />
                )}

                <TextField
                  fullWidth
                  label="Description"
                  value={descVal}
                  onChange={(e) => {
                    const v = e.target.value.slice(0, 255);
                    updateCell(idx, descriptionFieldName, v);
                  }}
                  inputProps={{ maxLength: 255 }}
                  error={showErrors && !!rowErrors[descriptionFieldName]}
                  helperText={
                    showErrors
                      ? rowErrors[descriptionFieldName] || `${descVal.length}/255`
                      : `${descVal.length}/255`
                  }
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addRule}
          disabled={items.length >= maxCount}
          sx={{ textTransform: "none" }}
        >
          Add {field.fieldLabel?.replace("Rules", "").trim()} Rule
        </Button>
        <Typography variant="caption" color="text.secondary">
          <InfoIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: "middle" }} />
          You can add up to {Math.max(0, maxCount - items.length)} more {direction} rules.
        </Typography>
      </Box>
    </Box>
  );
};

/* Small labelled wrapper so grid cells align consistently */
const FieldWrap = ({ label, children }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
    <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
      {label}{" "}
      <Tooltip title={label}>
        <InfoIcon sx={{ fontSize: 12, verticalAlign: "middle", opacity: 0.6 }} />
      </Tooltip>
    </Typography>
    {children}
  </Box>
);

/* ============================================================= */
/* Business Rules — Validators & Payload helpers                  */
/* ============================================================= */

const ALLOWED_PROTOCOLS = ["tcp", "udp", "icmp", "icmpv6", "all", "-1"];
const SENSITIVE_PORTS = [22, 23, 3389, 3306, 5432, 1433, 27017, 6379, 9200, 5601];

const IPV4_CIDR_RE =
  /^((25[0-5]|2[0-4]\d|[01]?\d?\d)\.){3}(25[0-5]|2[0-4]\d|[01]?\d?\d)\/(3[0-2]|[12]?\d)$/;
const IPV6_CIDR_RE = /^([0-9a-fA-F:]+)\/(1[0-2]\d|12[0-8]|\d{1,2})$/;
const SG_ID_RE = /^sg-[0-9a-f]{8}([0-9a-f]{9})?$/i;
const PL_ID_RE = /^pl-[0-9a-f]{8,17}$/i;
const PORT_INPUT_RE = /^(\d{1,5})(-\d{1,5})?$/;

export const isValidCIDR = (v) =>
  typeof v === "string" &&
  (IPV4_CIDR_RE.test(v.trim()) || IPV6_CIDR_RE.test(v.trim()));

export const isOpenToWorld = (v) =>
  typeof v === "string" && (v.trim() === "0.0.0.0/0" || v.trim() === "::/0");

export const parsePortRange = (raw) => {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (s.toLowerCase() === "all" || s === "-1") return { from: 0, to: 65535 };
  if (/^\d+$/.test(s)) {
    const n = Number(s);
    return { from: n, to: n };
  }
  const m = s.match(/^(\d+)\s*-\s*(\d+)$/);
  if (!m) return null;
  return { from: Number(m[1]), to: Number(m[2]) };
};

export const isValidPortRange = (raw) => {
  const s = String(raw ?? "").trim();
  if (!s) return false;
  if (s.toLowerCase() === "all" || s === "-1") return true;
  if (!PORT_INPUT_RE.test(s)) return false;
  const p = parsePortRange(s);
  if (!p) return false;
  if (p.from < 0 || p.to > 65535) return false;
  if (p.from > p.to) return false;
  return true;
};

export const isValidProtocol = (raw) =>
  typeof raw === "string" &&
  ALLOWED_PROTOCOLS.includes(raw.trim().toLowerCase());

export const validateSourceDestValue = (typeVal, value) => {
  const v = (value || "").trim();
  const t = (typeVal || "").toLowerCase();
  // if (!v)
  //   return "CIDR block, a security group ID or a prefix list has to be specified.";
  if (!v) {
    return "";
  }
  if (t.includes("securitygroup") || t === "sg") {
    return SG_ID_RE.test(v)
      ? ""
      : "CIDR block, a security group ID or a prefix list has to be specified.";
  }
  if (t.includes("prefix") || t.startsWith("pl")) {
    return PL_ID_RE.test(v)
      ? ""
      : "CIDR block, a security group ID or a prefix list has to be specified.";
  }
  if (isValidCIDR(v) || SG_ID_RE.test(v) || PL_ID_RE.test(v)) return "";
  return "CIDR block, a security group ID or a prefix list has to be specified.";
};

const ruleSignature = (row, direction) => {
  const t = direction === "inbound" ? "Inbound" : "Outbound";
  return [
    row?.[`${t}Type`]?.value,
    (row?.[`${t}Protocol`]?.value || "").toLowerCase(),
    row?.[`${t}PortRange`]?.value,
    row?.[
      direction === "inbound" ? "InboundSourceType" : "OutboundDestinationType"
    ]?.value,
    (
      row?.[
        direction === "inbound"
          ? "InboundSourceValue"
          : "OutboundDestinationValue"
      ]?.value || ""
    ).trim(),
  ].join("|");
};

export const validateSGForNext = (formData) => {
  const inb = Array.isArray(formData?.InboundRules?.value)
    ? formData.InboundRules.value
    : [];
  const out = Array.isArray(formData?.OutboundRules?.value)
    ? formData.OutboundRules.value
    : [];

  const hasInboundRule = inb.some(
    (r) => (r?.InboundSourceValue?.value || "").trim() !== ""
  );
  const hasOutboundRule = out.some(
    (r) => (r?.OutboundDestinationValue?.value || "").trim() !== ""
  );

  if (!hasInboundRule && !hasOutboundRule) {
    return {
      isValid: false,
      error:
        "At least one rule (Inbound or Outbound) with a valid Source/Destination is required to continue.",
    };
  }
  return { isValid: true, error: "" };
};

export const validateSecurityGroupRules = (formConfig, formData, formSubmitted) => {
  const errors = {};
  const warnings = {};

  const groups = [
    { name: "InboundRules", direction: "inbound" },
    { name: "OutboundRules", direction: "outbound" },
  ];

  groups.forEach(({ name: groupName, direction }) => {
    const field = formConfig.find((f) => f.fieldName === groupName);
    if (!field) return;

    const rows = Array.isArray(formData?.[groupName]?.value)
      ? formData[groupName].value
      : [];

    const maxCount = field.maxCount || 60;
    const groupErr = {};
    const groupWarn = {};

    if (rows.length > maxCount) {
      groupErr._group = `Maximum ${maxCount} ${direction} rules allowed`;
    }

    const seen = new Map();
    const T = direction === "inbound" ? "Inbound" : "Outbound";
    const protoField = `${T}Protocol`;
    const portField = `${T}PortRange`;
    const srcDestTypeField =
      direction === "inbound" ? "InboundSourceType" : "OutboundDestinationType";
    const srcDestValueField =
      direction === "inbound"
        ? "InboundSourceValue"
        : "OutboundDestinationValue";
    const descField = `${T}Description`;

    rows.forEach((row, idx) => {
      const rowErr = {};
      const rowWarn = [];

      // (field.collectionFields || []).forEach((cf) => {
      //   if (!cf.isRequired) return;
      //   const v = row?.[cf.fieldName]?.value;
      //   if (v === undefined || v === null || String(v).trim() === "") {
      //     rowErr[cf.fieldName] = `${cf.fieldLabel} is required`;
      //   }
      // });



      (field.collectionFields || []).forEach((cf) => {
        if (!cf.isRequired) return;

        // Let custom validation handle these fields
        if (
          cf.fieldName === "OutboundDestinationValue" ||
          cf.fieldName === "InboundSourceValue"
        ) {
          return;
        }

        const v = row?.[cf.fieldName]?.value;

        if (v === undefined || v === null || String(v).trim() === "") {
          rowErr[cf.fieldName] = `${cf.fieldLabel} is required`;
        }
      });

      const proto = row?.[protoField]?.value;
      if (proto && !isValidProtocol(proto)) {
        rowErr[protoField] = `Protocol must be one of ${ALLOWED_PROTOCOLS.join(", ")}`;
      }

      // const port = row?.[portField]?.value;
      // if (port !== undefined && port !== null && String(port).trim() !== "") {
      //   if (!isValidPortRange(port)) {
      //     rowErr[portField] =
      //       "Port range needs to be a number between 0 and 65535 (e.g. 80 or 80-443).";
      //   }
      // }



      const port = row?.[portField]?.value;
      const isPortEditable = row?.__meta?.isPortEditable;

      if (
        isPortEditable &&
        port !== undefined &&
        port !== null &&
        String(port).trim() !== ""
      ) {
        if (!isValidPortRange(port)) {
          rowErr[portField] =
            "Port range needs to be a number between 0 and 65535 (e.g. 80 or 80-443).";
        }
      }

      // const sdType = row?.[srcDestTypeField]?.value;
      // const sdValue = row?.[srcDestValueField]?.value;
      // if (!rowErr[srcDestValueField]) {
      //   const msg = validateSourceDestValue(sdType, sdValue);
      //   if (msg) rowErr[srcDestValueField] = msg;
      // }

      const sdType = row?.[srcDestTypeField]?.value;
      const sdValue = row?.[srcDestValueField]?.value;

      if (String(sdValue || "").trim() !== "") {
        const msg = validateSourceDestValue(sdType, sdValue);

        if (msg) {
          rowErr[srcDestValueField] = msg;
        }
      }

      if (
        formSubmitted &&
        !String(sdValue || "").trim()
      ) {
        rowErr[srcDestValueField] = "Destination Value is required";
      }

      const sig = ruleSignature(row, direction);
      if (sig && sig.replace(/\|/g, "").trim() !== "") {
        if (seen.has(sig)) {
          rowErr[srcDestValueField] =
            rowErr[srcDestValueField] ||
            `Duplicate of rule #${seen.get(sig) + 1}`;
        } else {
          seen.set(sig, idx);
        }
      }

      // if (direction === "inbound" && isOpenToWorld(sdValue)) {
      //   const pr = parsePortRange(port);
      //   if (pr) {
      //     const exposed = SENSITIVE_PORTS.filter(
      //       (p) => p >= pr.from && p <= pr.to
      //     );
      //     if (exposed.length) {
      //       rowWarn.push(
      //         `Sensitive port${exposed.length > 1 ? "s" : ""} ${exposed.join(
      //           ", "
      //         )} exposed to the internet — restrict the source CIDR.`
      //       );
      //     }
      //   }
      // }

      const desc = row?.[descField]?.value;
      if (desc && String(desc).length > 255) {
        rowErr[descField] = "Description must be 255 characters or fewer";
      }

      if (Object.keys(rowErr).length) groupErr[idx] = rowErr;
      if (rowWarn.length) groupWarn[idx] = rowWarn;
    });

    if (Object.keys(groupErr).length) errors[groupName] = groupErr;
    if (Object.keys(groupWarn).length) warnings[groupName] = groupWarn;
  });

  return { errors, warnings };
};

const stripMeta = (row) => {
  const { __meta, ...rest } = row || {};
  return rest;
};

export const getSecurityGroupRulesPayload = (formData) => {
  const pack = (groupName) => {
    const rows = Array.isArray(formData?.[groupName]?.value)
      ? formData[groupName].value
      : [];
    return rows.map(stripMeta);
  };
  return {
    InboundRules: pack("InboundRules"),
    OutboundRules: pack("OutboundRules"),
  };
};

export default SecurityGroupRulesAccordion;